/**
 * The trade window: what you're offering, what they're offering, your pack to add from, and the
 * two-step accept (offer, then confirm). Plus the little prompt when someone asks to trade.
 */
import { ITEMS, isStackable, type ClientMsg, type ItemId, type SelfState, type TradeOffer, type TradeView } from '@sunderfen/shared';
import { itemIcon } from './icons.js';

const $ = <T extends HTMLElement = HTMLElement>(s: string) => document.querySelector(s) as T;
const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const fmt = (n: number) => n.toLocaleString('en-GB');

export class TradeUi {
  view: TradeView | null = null;
  private askTimer = 0;
  constructor(private readonly send: (m: ClientMsg) => void, private readonly self: () => SelfState) {
    $('#trade-decline').addEventListener('click', () => this.send({ t: 'tradeCancel' }));
    $('#trade-accept').addEventListener('click', () => this.send({ t: 'tradeAccept' }));
    $('#trade-coins-go').addEventListener('click', () => { const n = Math.floor(Number(($('#trade-coins') as HTMLInputElement).value) || 0); this.send({ t: 'tradeCoins', n }); });
    $('#trade-ask-no').addEventListener('click', () => { $('#trade-ask').hidden = true; });
  }

  /** Someone asked: a prompt with a button that asks them back (which opens the window). */
  asked(from: string, id: number) {
    const box = $('#trade-ask');
    $('#trade-ask-text').textContent = `${from} wants to trade with you.`;
    const yes = $('#trade-ask-yes');
    yes.onclick = () => { this.send({ t: 'tradeAsk', id }); box.hidden = true; };
    box.hidden = false;
    clearTimeout(this.askTimer);
    this.askTimer = window.setTimeout(() => { box.hidden = true; }, 60000);
  }

  show(v: TradeView | null) {
    this.view = v;
    const box = $('#trade');
    if (!v) { box.hidden = true; return; }
    $('#trade-ask').hidden = true;
    box.hidden = false;
    this.render();
  }

  /** Re-draw (the pack changed, or the offer did). */
  render() {
    const v = this.view;
    if (!v) return;
    const confirm = v.stage === 'confirm';
    $('#trade-title').textContent = confirm ? `Confirm the trade with ${v.withName}` : `Trading with ${v.withName}`;
    $('#trade-sub').textContent = confirm ? 'Check exactly what you give and what you get. Any change goes back a step.' : 'Click your things to put them on the table. Both accept, then both confirm.';
    const lines = (o: TradeOffer, mine: boolean) => {
      const el = document.createElement('div'); el.className = 'tr-lines';
      if (!o.items.length && !o.coins) el.innerHTML = '<p class="muted" style="margin:6px 0;font-size:12.5px">Nothing yet.</p>';
      o.items.forEach((x, i) => {
        const b = document.createElement('button'); b.type = 'button'; b.className = 'tr-line'; b.disabled = !mine || confirm;
        b.title = mine && !confirm ? 'Take it back off the table' : '';
        b.innerHTML = `<img alt="" src="${itemIcon(x.item)}"><span>${esc(ITEMS[x.item]?.name ?? x.item)}</span>${x.n > 1 ? `<b class="num">× ${fmt(x.n)}</b>` : ''}`;
        b.onclick = () => this.send({ t: 'tradeRemove', i });
        el.appendChild(b);
      });
      if (o.coins) el.insertAdjacentHTML('beforeend', `<div class="tr-line coins"><span>Coins</span><b class="num">${fmt(o.coins)}</b></div>`);
      return el;
    };
    const mine = $('#trade-mine'), theirs = $('#trade-theirs');
    mine.innerHTML = `<div class="sect">${confirm ? 'You give' : 'You offer'}${v.iAccepted ? ' <span class="ok">accepted</span>' : ''}</div>`;
    theirs.innerHTML = `<div class="sect">${confirm ? 'You get' : `${esc(v.withName)} offers`}${v.theyAccepted ? ' <span class="ok">accepted</span>' : ''}</div>`;
    mine.appendChild(lines(v.mine, true)); theirs.appendChild(lines(v.theirs, false));
    // your pack, to add from (offered things are dimmed by how many are already out)
    const pack = $('#trade-pack'), s = this.self();
    pack.innerHTML = '';
    pack.hidden = confirm; $('#trade-coins-row').hidden = confirm;
    s.inv.forEach((id, slot) => {
      if (!id) return;
      const offered = v.mine.items.filter(x => x.item === id).reduce((a, x) => a + x.n, 0);
      const have = isStackable(id as ItemId) ? s.stacks[id] ?? 0 : s.inv.filter(x => x === id).length;
      const b = document.createElement('button'); b.type = 'button'; b.className = 'slot';
      const quest = ITEMS[id]?.cat === 'quest';
      b.disabled = quest || offered >= have;
      b.title = quest ? `${ITEMS[id].name} (can't be traded)` : `Offer ${ITEMS[id].name}`;
      const n = isStackable(id as ItemId) ? s.stacks[id] ?? 0 : 0;
      b.innerHTML = `<img alt="" src="${itemIcon(id)}">${n ? `<span class="qty num">${fmt(n)}</span>` : ''}`;
      b.onclick = () => this.send({ t: 'tradeAdd', slot });
      pack.appendChild(b);
    });
    const coins = $('#trade-coins') as HTMLInputElement;
    coins.max = String(s.coins); coins.placeholder = `${fmt(s.coins)} coins`;
    $('#trade-note').textContent = v.note ?? (v.theyAccepted && !v.iAccepted ? `${v.withName} has ${confirm ? 'confirmed' : 'accepted'}.` : v.iAccepted && !v.theyAccepted ? `Waiting for ${v.withName}...` : '');
    const acc = $<HTMLButtonElement>('#trade-accept');
    acc.textContent = confirm ? 'Confirm trade' : 'Accept';
    acc.disabled = v.iAccepted;
  }
}
